import {
  Component,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  HostListener,
  DoCheck
} from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { delay } from 'rxjs/operators';

import { BaseViewItemComponent } from '../_basic/item-view-base.component';
import { UIService } from '../../core/services/utils/ui.service';
import { MonitorMenuService } from '../../core/services/dialog-services/monitor-menu.service';
import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { MonitorsService } from '../../core/services/http-services/monitors.service';
import { IPlaylistMonitorInfo } from '../../core/interfaces/playlist-monitor-info.interface';
import { MonitorMenuDialogDessagesEnum } from '../../core/enums/toastr-messages/monitor-menu-dialog-messages.enum';
import { ScreenFrame } from '../../core/models/signalR-models/screen-frame';
import { ViewMode } from '../../core/enums/view-mode.enum';
import { MonitorChangeViewService } from '../../core/services/http-services/monitor-change-view.service';
import { DropItem } from '../../core/interfaces/drop-item.model';
import { recalculateElementMaxWidthAndHeight } from '../../helpers/recalculate-element-max-width-and-height.helper';
import { LibraryEditItemTypeEnum } from '../../core/enums/library-edit-item-type.enum';

@Component({
  selector: 'monitor',
  templateUrl: './monitor.component.html',
  styleUrls: ['./monitor.component.scss']
})
export class MonitorComponent extends BaseViewItemComponent
  implements OnDestroy, DoCheck {
  @Input() playlistDrugActive: boolean;
  @Input() viewMode: ViewMode;
  @Input() isFullScreen: boolean;
  @Input() showScreenName: boolean = true;
  @Input() isSelectionEnable: boolean = true;
  @Output() playlistDropped: EventEmitter<IPlaylistMonitorInfo> = new EventEmitter();
  @Output() viewModeChanged: EventEmitter<ViewMode> = new EventEmitter();
  @ViewChild('monitorName') monitorNameRef: ElementRef;

  isDropEnabled: boolean;
  isDropActive: boolean;
  updateNameMode: boolean = false;
  viewModes = ViewMode;
  monitorMaxWidth: string;
  public isIdentifyEnabled: boolean;

  constructor(
    public monitorDataService: MonitorsService,
    public selectionService: ScreensStateService,
    public monitorMenuService: MonitorMenuService,
    public uiService: UIService,
    private readonly toastrService: ToastrService,
    private readonly monitorChangeViewService: MonitorChangeViewService
  ) {
    super(selectionService, monitorMenuService);

    const playlistSubscription = this.uiService.itemlistDragSubject.subscribe((dragData: DropItem) => {
      this.isDropEnabled = dragData
        && this.isSelectionEnable
        && this.monitor
        && (!dragData.data.MachinePhysicalAddress || dragData.data.MachinePhysicalAddress === this.monitor.MachinePhysicalAddress)
        && (dragData.type === LibraryEditItemTypeEnum.EditPlaylist ?
          dragData.data.IsAndroid === this.monitor.Machine.IsAndroid :
          true);
    });

    const dragSubscription = this.uiService.dragActivationSubject.subscribe(
      state => {
        this.isDropActive = state && this.isDropEnabled && this.isSelected && this.isSelectionEnable;
      }
    );

    this.subscriptions.add(playlistSubscription);
    this.subscriptions.add(dragSubscription);

    this.subscriptions.add(
      this.uiService.bottomPanelVisibleSubject.pipe(
        delay(1000)
      ).subscribe(() => recalculateElementMaxWidthAndHeight(this.viewMode, this.monitor.MonitorHandler, true))
    );
  }

  @HostListener('document:click')
  public onBlurInputName() {
    if (this.monitorNameRef) {
      this.monitorNameRef.nativeElement.blur();
    }
  }

  ngDoCheck(): void {
    recalculateElementMaxWidthAndHeight(this.viewMode, this.monitor.MonitorHandler);
  }

  mouseLeave() {
    if (this.isDropEnabled && this.isSelectionEnable) {
      this.uiService.handleDragActivation(false);
    }
    this.isDropActive = this.isSelected && this.isDropActive;
  }

  mouseEnter() {
    if (this.isDropEnabled && this.isSelectionEnable) {
      this.uiService.handleDragActivation(true);
    }
    this.isDropActive = this.isDropEnabled;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  monitorClick(event: MouseEvent): void {
    if (this.isSelectionEnable) {
      event.stopPropagation();
      const callFunction = !this.isSelected ? 'selectScreen' : 'unselectScreen';

      this.monitorMenuService.monitorContextMenuInstance?.componentInstance ?
        this.monitorMenuService.monitorContextMenuInstance.close() :
        this.selectionService[callFunction](this.monitor, event.ctrlKey);
    } else {
      this.onDoubleClickHandler();
    }
  }

  enterPredicate(drag: DropItem, drop: DropItem): boolean {
    return (
      drag.data &&
      (!drag.data.MachinePhysicalAddress ||
        drag.data.MachinePhysicalAddress === drop.data.MachinePhysicalAddress)
    );
  }

  // With this function we forbid the user to drop data to the full-screen carousel monitors
  falsePredicateFunction = () => false;

  drop(event: CdkDragDrop<string[]>): void {
    this.selectionService.dropItem(event, this.monitor, this.isSelected);
  }

  onContextMenu(event): void {
    if (this.isSelectionEnable) {
      event.preventDefault();
      event.stopPropagation();

      this.selectionService['selectScreen'](this.monitor, event.ctrlKey);
      if (!this.uiService.bottomPanelPinnedSubject.value) {
        this.uiService.bottomPanelVisibleSubject.next(false);
      }

      this.monitorMenuService.showMonitorMenu(
        this.monitor,
        event.clientX,
        event.clientY,
        false
      );
    }
  }

  playNext(event: MouseEvent): void {
    event.stopPropagation();
    this.monitor.playNext();
  }

  playPrevious(event: MouseEvent): void {
    event.stopPropagation();
    this.monitor.playPrevious();
  }

  onDoubleClickHandler(): void {
    this.monitorChangeViewService.selectedForFullScreenChanged.next(this.monitor);
    this.viewModeChanged.next(ViewMode.FullScreen);
    this.selectionService.unselectAll();
    this.uiService.hideBottomPanel();
  }

  saveMonitorTitle(name?: string): void {
    if (name && !!name.trim()) {
      this.monitor.MonitorTitle = name;
      this.subscriptions.add(
        this.monitorDataService.updateTitle(this.monitor.MonitorHandler,
          this.monitor.MachinePhysicalAddress, this.monitor.MonitorTitle).subscribe(() => {
            this.toastrService.success(`${MonitorMenuDialogDessagesEnum.MonitorRenamedDescription} '${this.monitor.MonitorTitle}'`,
              MonitorMenuDialogDessagesEnum.MonitorRenamedTitle);
          })
      );
    }
    this.updateNameMode = false;
  }

  getApplicationTitle(screenFrame: ScreenFrame): string {
    // Here we delete monitorHandler guid from the name of app
    if (screenFrame.ApplicationTitle && screenFrame.ApplicationTitle.includes(screenFrame.MonitorHandler)) {
      const match: RegExpMatchArray = screenFrame.ApplicationTitle.match(screenFrame.MonitorHandler);

      return screenFrame.ApplicationTitle.substring(0, match.index - 1);
    }

    return screenFrame.ApplicationTitle;
  }

  @HostListener('window:resize') onResize(): void {
    recalculateElementMaxWidthAndHeight(this.viewMode, this.monitor.MonitorHandler, true);
  }
}
